import { useConsentManager } from '@c15t/nextjs/headless';
import { useIntl } from 'react-intl';

import { COOKIE_NOTICE_CATEGORIES } from './categories';
import { useConsentUiContext } from './ConsentUiContext';
import type { ConsentCategoryName, ConsentUIView } from './types';

/** Feature gating + open preferences. Use inside ConsentManager. */
export const useConsent = () => {
  const { has } = useConsentManager();
  const { openPreferences } = useConsentUiContext();

  return {
    hasConsent: (category: ConsentCategoryName) => has(category),
    hasFunctionalityConsent: has('functionality'),
    hasMeasurementConsent: has('measurement'),
    hasMarketingConsent: has('marketing'),
    hasExperienceConsent: has('experience'),
    openPreferences,
  };
};

/** Maps c15t state into CookieNotice props (views, chrome, category rows). */
export const useConsentController = () => {
  const intl = useIntl();
  const { activeUI, selectedConsents, setSelectedConsent, saveConsents } =
    useConsentManager();
  const { customizeEntry, showCustomizeFromBanner, showNotice, dismiss } =
    useConsentUiContext();

  const view: ConsentUIView = activeUI === 'dialog' ? 'customize' : 'notice';
  const isOpen = activeUI === 'banner' || activeUI === 'dialog';
  const showBack = view === 'customize' && customizeEntry === 'banner';
  const showClose = view === 'customize' && customizeEntry === 'direct';

  const categories = COOKIE_NOTICE_CATEGORIES.map((category) => ({
    id: category.name,
    label: intl.formatMessage({ id: category.labelId }),
    checked: !!selectedConsents[category.name],
    disabled: !!category.locked,
    onChange: category.locked
      ? undefined
      : (checked: boolean) => {
          setSelectedConsent(category.name, checked);
          // UI shows one "Preference & functionality" row; keep experience in sync.
          if (category.name === 'functionality') {
            setSelectedConsent('experience', checked);
          }
        },
  }));

  return {
    view,
    isOpen,
    showBack,
    showClose,
    categories,
    showNotice,
    showCustomize: showCustomizeFromBanner,
    dismiss,
    allowAll: () => {
      void saveConsents('all');
    },
    declineAll: () => {
      void saveConsents('necessary');
    },
    saveCustom: () => {
      void saveConsents('custom');
    },
  };
};
